import * as Sentry from "@sentry/react";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

export function initSentry() {
  if (!SENTRY_DSN) {
    console.info("[Sentry] DSN not configured — error tracking disabled.");
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: `medicorepay-web@${import.meta.env.VITE_APP_VERSION || "0.0.0"}`,

    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,

    // Session Replay (only in production)
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,

    // Only send errors in non-development environments
    enabled: import.meta.env.PROD || !!import.meta.env.VITE_SENTRY_DSN,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Filter out noise
    ignoreErrors: [
      // Browser extension errors
      "top.GLOBALS",
      "chrome-extension://",
      "moz-extension://",
      // Network errors
      "Network Error",
      "Failed to fetch",
      "Load failed",
      "NetworkError",
      // User-triggered
      "AbortError",
      "ResizeObserver loop",
      // PWA
      "ServiceWorker",
    ],

    beforeSend(event) {
      // Don't send errors in development unless explicitly configured
      if (import.meta.env.DEV && !import.meta.env.VITE_SENTRY_DSN) {
        return null;
      }
      return event;
    },
  });

  console.info("[Sentry] Error tracking initialized.");
}

/**
 * Set the current user context for Sentry error reports.
 */
export function setSentryUser(user: {
  id: string;
  email?: string;
  tenantId?: string;
  role?: string;
}) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
  });
  if (user.tenantId) {
    Sentry.setTag("tenant_id", user.tenantId);
  }
  if (user.role) {
    Sentry.setTag("user_role", user.role);
  }
}

/**
 * Clear user context (on logout).
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Sentry error boundary component for React.
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;
